import { alignItems, flex, fontSize, padding, styled } from '@mui/system';
import {
	DataGrid,
	DataGridProps,
	GridRowIdGetter,
	GridValidRowModel,
	GridRowSelectionModel,
	GridCallbackDetails,
	GridColDef,
	GridRowId,
	gridClasses,
} from '@mui/x-data-grid';
import { Control, useController, useFormContext } from 'react-hook-form';
import { Pagination } from '../../../api/DTO/common.interface';
import { Box, SxProps, Theme } from '@mui/material';
import { useTranslation } from 'react-i18next';

export const initialPagination: Pagination = {
	page: 1,
	size: 50,
};

interface AppDataGridProps {
	loading: boolean;
	rows: GridValidRowModel[];
	columns: GridColDef[];
	rowCount: number;
	getRowId: GridRowIdGetter<GridValidRowModel> | undefined;
	checkboxSelection?: boolean;
	rowSelectionModel?: GridRowSelectionModel;
	onRowSelectionModelChange?:
		| ((
				selectionModel: GridRowSelectionModel,
				details: GridCallbackDetails<any>
		  ) => void)
		| undefined;
	columnHeadersBackgroundColor?: string;
	control?: Control<any, any>;
	/** Per-row class hook, for styling rows by their data (e.g. unread rows). */
	getRowClassName?: DataGridProps['getRowClassName'];
	/** Escape hatch for the caller to style the classes it sets above. */
	sx?: SxProps<Theme>;
}

/**
 * Custom Data Grid with React Hook Form controlled pagination
 */
export default function AppDataGrid({
	loading,
	rows,
	columns,
	rowCount,
	getRowId,
	checkboxSelection,
	rowSelectionModel,
	onRowSelectionModelChange,
	columnHeadersBackgroundColor,
	control,
	getRowClassName,
	sx,
}: AppDataGridProps) {
	// const { control } = useFormContext();
	const { field } = useController({ control: control, name: 'pagination' });
	const pagination = field.value as Pagination;
	const paginationOnChange = field.onChange;
	const { t } = useTranslation();

	return (
		<StyledDataGrid
			loading={loading}
			rows={rows}
			columns={columns}
			getRowId={getRowId}
			rowCount={rowCount}
			paginationMode='server'
			paginationModel={{
				page: pagination.page - 1,
				pageSize: pagination.size,
			}}
			onPaginationModelChange={(newModel) => {
				paginationOnChange({
					page: newModel.page + 1, // 转换页码
					size: newModel.pageSize,
				});
			}}
			pagination={true}
			getRowHeight={() => 'auto'}
			pageSizeOptions={[5, 10, 20, 50, 100]}
			disableRowSelectionOnClick
			disableColumnFilter
			disableColumnSelector
			disableDensitySelector
			disableColumnMenu
			sortingMode='server'
			checkboxSelection={checkboxSelection ?? false}
			rowSelectionModel={rowSelectionModel}
			onRowSelectionModelChange={onRowSelectionModelChange}
			keepNonExistentRowsSelected
			// columnHeaderHeight={56}
			autosizeOnMount
			columnHeadersBackgroundColor={columnHeadersBackgroundColor}
			getRowClassName={getRowClassName}
			sx={sx}
			localeText={{
				paginationDisplayedRows: ({ from, to, count }: any) => {
					return `${from}-${to} results, total ${
						count !== -1 ? count : `${to}`
					} results`;
				},
				paginationRowsPerPage: 'Results on page:',
			}}
			slots={{
				noRowsOverlay: () => (
					<Box
						display={'flex'}
						flexDirection='column'
						justifyContent='center'
						alignItems='center'
						height={'100%'}
					>
						{t('common.tableNotFound')}
					</Box>
				),
			}}
		/>
	);
}

const StyledDataGrid = styled(DataGrid, {
	shouldForwardProp: (prop) => prop !== 'columnHeadersBackgroundColor',
})<{
	columnHeadersBackgroundColor?: string;
}>(({ columnHeadersBackgroundColor }) => ({
	border: 0,
	'& .MuiDataGrid-row--borderBottom': {
		height: 'auto !important',
		minHeight: '56px',
	},
	'& .MuiDataGrid-columnHeader': {
		height: 'auto !important',
		minHeight: '56px',
	},
	'& .MuiDataGrid-columnHeaderTitle': {
		fontWeight: 'normal',
		whiteSpace: 'normal !important',
		wordWrap: 'break-word',
		overflow: 'visible !important',
		paddingTop: '8px',
		paddingBottom: '8px',
	},
	'& .MuiDataGrid-columnHeaders': {
		backgroundColor: columnHeadersBackgroundColor
			? columnHeadersBackgroundColor
			: '#fafafa!important',
		fontWeight: 'normal',
		height: 'auto !important',
		minHeight: '56px',
	},
	'--DataGrid-t-header-background-base': '#fafafa!important',
	'& .MuiDataGrid-columnSeparator': {
		visibility: 'hidden',
	},
	'& .MuiDataGrid-iconButtonContainer button': {
		display: 'none',
	},
	'& .MuiDataGrid-cell': {
		display: 'flex',
		alignItems: 'center',
		paddingTop: '10px',
		paddingBottom: '10px',

		whiteSpace: 'normal !important',
		wordBreak: 'break-word !important',
		overflow: 'visible !important',
		textOverflow: 'unset !important',
	},
	'& .MuiDataGrid-cellContent': {
		whiteSpace: 'normal !important',
		wordBreak: 'break-word !important',
		overflow: 'visible !important',
	},
}));
